import { Box, IconButton, Stack, Typography, useTheme, alpha } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTranslation } from "react-i18next";
import type { RoadmapPhase, RoadmapTask, RoadmapWeek } from "../../api/client";

interface RoadmapStickyHeaderProps {
  currentTask: RoadmapTask | null;
  currentPhase: RoadmapPhase | null;
  currentWeek: RoadmapWeek | null;
  nextMilestone: { label: string; weekIndex: number } | null;
}

const HEIGHT = 72;
const TOTAL_WEEKS = 12;

// Spec §6.2 — 72px sticky header with blur backdrop. Three-column grid:
// eyebrow + focus-task title (left, truncates) · milestone pill (hides
// <900px) · icon actions (filter/edit/menu — all deferred to v1.1 per
// spec §10, rendered disabled). The coral pulse dot in the eyebrow is the
// ONLY animation-worthy element in v1; honors prefers-reduced-motion.
//
// Title is `<h2>` per a11y addition spec §8 — gives screen-reader landmark
// navigation for the page's "current focus" anchor.
export default function RoadmapStickyHeader({
  currentTask,
  currentPhase,
  currentWeek,
  nextMilestone,
}: RoadmapStickyHeaderProps) {
  const theme = useTheme();
  const { t } = useTranslation("roadmap");

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        height: HEIGHT,
        display: "grid",
        gridTemplateColumns: { xs: "1fr auto", md: "1fr auto auto" },
        alignItems: "center",
        columnGap: 2,
        px: { xs: 2, sm: 3 },
        bgcolor: alpha(theme.palette.background.paper, 0.85),
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Left: eyebrow + focus task title */}
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
          <PulseDot />
          <Typography
            component="span"
            variant="caption"
            sx={{
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "text.secondary",
              fontSize: 10.5,
            }}
          >
            {currentPhase && currentWeek
              ? t("RoadmapStickyHeader.currentFocus", {
                  index: currentWeek.index,
                  total: TOTAL_WEEKS,
                  phaseTitle: currentPhase.title,
                })
              : t("RoadmapStickyHeader.noActiveFocus")}
          </Typography>
        </Stack>
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{
            fontWeight: 500,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "text.primary",
          }}
        >
          {currentTask?.title ?? t("RoadmapStickyHeader.pickUpWhereLeftOff")}
        </Typography>
      </Box>

      {/* Milestone pill — hidden <900px (md breakpoint) per spec §6.2 */}
      {nextMilestone && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            display: { xs: "none", md: "inline-flex" },
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
            border: "1px solid",
            borderColor: alpha(theme.palette.secondary.main, 0.4),
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
            maxWidth: 360,
          }}
        >
          <StarIcon sx={{ fontSize: 14, color: "secondary.main" }} aria-hidden="true" />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
              {t("RoadmapStickyHeader.nextMilestoneLabel")}
            </Box>
            {nextMilestone.label}
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                color: "text.secondary",
                ml: 0.75,
                fontSize: 10.5,
              }}
            >
              {t("RoadmapStickyHeader.endOfWeek", { week: nextMilestone.weekIndex })}
            </Box>
          </Typography>
        </Stack>
      )}

      {/* Icon actions — all deferred per spec §10. Rendered visually as
          chrome but disabled. */}
      <Stack direction="row" spacing={0.5}>
        <IconButton
          size="small"
          aria-label={t("RoadmapStickyHeader.filterTasks")}
          disabled
          sx={{ color: "text.secondary", opacity: 0.45 }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("RoadmapStickyHeader.editRoadmap")}
          disabled
          sx={{ color: "text.secondary", opacity: 0.45 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("RoadmapStickyHeader.moreOptions")}
          disabled
          sx={{ color: "text.secondary", opacity: 0.45 }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

// Coral pulse dot — the single animated element on the page. CSS keyframes
// inline so the component is self-contained. prefers-reduced-motion turns
// the pulse off but keeps the dot visible.
function PulseDot() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: "secondary.main",
        flexShrink: 0,
        animation: "cf-pulse-dot 1.8s ease-in-out infinite",
        "@keyframes cf-pulse-dot": {
          "0%, 100%": { opacity: 1, transform: "scale(1)" },
          "50%": { opacity: 0.45, transform: "scale(0.85)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    />
  );
}
